import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Tooltip,
  Switch,
  FormControlLabel,
  InputAdornment,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Email,
  Save,
  Cancel,
  ContentCopy,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  getEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate
} from '../../redux/actions/communicationActions';

const EmailTemplateManager = () => {
  const dispatch = useDispatch();
  const { emailTemplates, loading, error } = useSelector((state) => state.communication || {});
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateData, setTemplateData] = useState({
    name: '',
    subject: '',
    body: '',
    type: 'general',
    is_active: true,
    variables: []
  });
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    dispatch(getEmailTemplates());
  }, [dispatch]);

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateData({
        name: template.name || '',
        subject: template.subject || '',
        body: template.body || '',
        type: template.type || 'general',
        is_active: template.is_active !== undefined ? template.is_active : true,
        variables: template.variables || []
      });
    } else {
      setEditingTemplate(null);
      setTemplateData({
        name: '',
        subject: '',
        body: '',
        type: 'general',
        is_active: true,
        variables: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTemplate(null);
    setTemplateData({
      name: '',
      subject: '',
      body: '',
      type: 'general',
      is_active: true,
      variables: []
    });
  };

  const handleSaveTemplate = async () => {
    if (editingTemplate) {
      const result = await dispatch(updateEmailTemplate(editingTemplate.id, templateData));
      if (result.success) {
        handleCloseDialog();
        dispatch(getEmailTemplates());
      }
    } else {
      const result = await dispatch(createEmailTemplate(templateData));
      if (result.success) {
        handleCloseDialog();
        dispatch(getEmailTemplates());
      }
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      const result = await dispatch(deleteEmailTemplate(id));
      if (result.success) {
        dispatch(getEmailTemplates());
      }
    }
  };

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setPreviewDialog(true);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'invoice': return 'primary';
      case 'offer': return 'success';
      case 'birthday': return 'warning';
      case 'general': return 'default';
      default: return 'default';
    }
  };

  const templateTypes = [
    { value: 'invoice', label: 'Invoice' },
    { value: 'offer', label: 'Offer' },
    { value: 'birthday', label: 'Birthday' },
    { value: 'general', label: 'General' }
  ];

  const getAvailableVariables = (type) => {
    switch (type) {
      case 'invoice':
        return '{customer_name}, {invoice_number}, {invoice_date}, {total_amount}, {company_name}, {items_list}';
      case 'birthday':
        return '{customer_name}, {discount_percentage}, {company_name}, {valid_until}';
      case 'offer':
        return '{customer_name}, {offer_description}, {offer_end_date}, {company_name}';
      default:
        return '{customer_name}, {company_name}';
    }
  };

  const filteredTemplates = emailTemplates.filter(template => {
    const matchesSearch = template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.subject?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || template.type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading && emailTemplates.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Email Templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage email templates for customer communications
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #653d91 100%)',
            }
          }}
        >
          Add Template
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Visibility />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Type</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="Filter by Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {templateTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Templates Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Template Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No templates found. Create your first template to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTemplates.map((template) => (
                <TableRow 
                  key={template.id}
                  sx={{ 
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:last-child td': { border: 0 }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                        <Email sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Typography variant="body2" fontWeight="medium">
                        {template.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={template.type}
                      color={getTypeColor(template.type)}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                      {template.subject}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={template.is_active ? 'Active' : 'Inactive'}
                      color={template.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(template.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Preview">
                        <IconButton
                          size="small"
                          onClick={() => handlePreviewTemplate(template)}
                          color="info"
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(template)}
                          color="primary"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteTemplate(template.id)}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Template Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2.5,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
              <Email sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {editingTemplate ? 'Edit Email Template' : 'Create Email Template'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {editingTemplate ? 'Update your email template details' : 'Design a new email template for customer communications'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, px: 3, pb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', border: '2px dashed', borderColor: 'primary.light' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                    <ContentCopy sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                      Template Information
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Basic details about your email template
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Template Name"
                      value={templateData.name}
                      onChange={(e) => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Invoice Email Template"
                      required
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white',
                          '&:hover': { bgcolor: 'grey.50' }
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ContentCopy color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Template Type</InputLabel>
                      <Select
                        value={templateData.type}
                        onChange={(e) => setTemplateData(prev => ({ ...prev, type: e.target.value }))}
                        label="Template Type"
                        sx={{
                          bgcolor: 'white',
                          '&:hover': { bgcolor: 'grey.50' }
                        }}
                      >
                        {templateTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            <Chip 
                              label={type.label} 
                              size="small" 
                              color={getTypeColor(type.value)}
                              sx={{ mr: 1 }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', border: '2px dashed', borderColor: 'info.light' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'info.main', width: 36, height: 36 }}>
                    <Email sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" color="info.main">
                      Email Content
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Subject and body of your email template
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Email Subject"
                    value={templateData.subject}
                    onChange={(e) => setTemplateData(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="e.g., Invoice #{invoice_number} from {company_name}"
                    required
                    variant="outlined"
                    helperText={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Typography variant="caption">Use variables:</Typography>
                        <Chip size="small" label="{invoice_number}" sx={{ height: 20, fontSize: '0.65rem' }} />
                        <Chip size="small" label="{customer_name}" sx={{ height: 20, fontSize: '0.65rem' }} />
                        <Chip size="small" label="{company_name}" sx={{ height: 20, fontSize: '0.65rem' }} />
                      </Box>
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '&:hover': { bgcolor: 'grey.50' }
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Email Body"
                    multiline
                    rows={14}
                    value={templateData.body}
                    onChange={(e) => setTemplateData(prev => ({ ...prev, body: e.target.value }))}
                    placeholder="Enter your email template here. Use variables like {customer_name}, {invoice_number}, etc."
                    required
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '&:hover': { bgcolor: 'grey.50' },
                        fontFamily: 'monospace',
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: 'info.light', border: '2px solid', borderColor: 'info.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'info.main', width: 36, height: 36, mt: 0.5 }}>
                    <Visibility sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: 'info.dark' }}>
                      Available Variables:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {getAvailableVariables(templateData.type).split(', ').map((variable, index) => (
                        <Chip
                          key={index}
                          label={variable}
                          size="small"
                          sx={{
                            bgcolor: 'white',
                            color: 'info.dark',
                            fontWeight: 500,
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            '&:hover': {
                              bgcolor: 'info.main',
                              color: 'white'
                            }
                          }}
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Click on variables to copy them or type them directly in the template
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={templateData.is_active}
                      onChange={(e) => setTemplateData(prev => ({ ...prev, is_active: e.target.checked }))}
                      color="primary"
                      size="medium"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Active Template
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Only active templates can be used in campaigns
                      </Typography>
                    </Box>
                  }
                />
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, bgcolor: 'grey.50' }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            startIcon={<Cancel />}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              px: 3,
              borderColor: 'grey.400',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'grey.600',
                bgcolor: 'grey.100'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveTemplate}
            variant="contained"
            disabled={!templateData.name || !templateData.subject || !templateData.body}
            startIcon={<Save />}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              px: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653d91 100%)',
              },
              '&:disabled': {
                background: 'grey.300'
              }
            }}
          >
            {editingTemplate ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog 
        open={previewDialog} 
        onClose={() => setPreviewDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'info.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Visibility />
          <Typography variant="h6" fontWeight="bold">
            Template Preview
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {previewTemplate && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {previewTemplate.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip
                    label={previewTemplate.type}
                    color={getTypeColor(previewTemplate.type)}
                    size="small"
                  />
                  <Chip
                    label={previewTemplate.is_active ? 'Active' : 'Inactive'}
                    color={previewTemplate.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                <Divider />
              </Box>
              
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Subject:
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
                {previewTemplate.subject}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Body:
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: 'grey.50',
                  maxHeight: 400,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {previewTemplate.body}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button 
            onClick={() => setPreviewDialog(false)}
            variant="contained"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailTemplateManager;
